import styled from 'styled-components'
import { rgba } from 'polished'
import { Text, Box } from 'components/Box'
import Dropdown from 'components/Dropdown'
import BaseCheckbox from 'components/Checkbox'
import { SelectPopover, SelectOption } from 'components/Select'

// Icons
import { ReactComponent as ChevronVertical } from 'icons/chevron-vertical-16.svg'
import { ReactComponent as CheckmarkIcon } from 'icons/check-12.svg'

export const ToggleWrapper = styled.div<{
  isEmphasized?: boolean
  disabled?: boolean
}>`
  width: 125px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px; /* stylelint-disable-line scales/space */
  height: 32px;
  margin: 0;
  border: 0;
  background: none;
  font-size: 0.8125rem;
  color: ${({ theme }) => rgba(theme.colors.black, 0.45)};
  transition: border-color
    ${({ theme }) =>
      `${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};
  cursor: pointer;

  :hover,
  :focus {
    background-color: ${({ theme }) => theme.colors.concrete};
    transition: background-color 0.3s ease-out;
  }

  ${({ theme }) => `
    border: 1px solid ${rgba(theme.colors.black, 0.15)};
    border-radius: 3px;
    box-shadow: ${theme.shadows.boxShadow.subtle};
  `}

  ${({ disabled }) =>
    disabled &&
    `
    // Allows hover events to be triggered on disabled elements https://github.com/facebook/react/issues/4251#issuecomment-529182276
    pointer-events: none;
    opacity: 0.5;
  `}

  ${({ isEmphasized }) =>
    isEmphasized &&
    `
    font-style: italic;
  `}
`

export const IconWrapper = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.oslogray};
  margin-left: 14px; /* stylelint-disable-line scales/space */
`

export const StyledChevron = styled(ChevronVertical)`
  width: 12px;
  height: 12px;
`

export const SecondaryText = styled(Text).attrs({
  color: 'text.dark.tertiary',
  mt: 1,
  mb: 0,
})``

export const Separator = styled(Box).attrs({
  height: 1,
  bg: 'border.dark.base',
  mt: 2,
  mb: -2,
  mr: -5,
})``

export const FullSeparator = styled(Box).attrs({
  height: 1,
  bg: 'border.dark.base',
})``

export const Checkmark = styled(CheckmarkIcon)`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  vertical-align: top;
`

export const Checkbox = styled(BaseCheckbox)`
  margin-top: 8px;

  small {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.dark.secondary};
  }
`

export const CommentDropdownItem = styled(Dropdown.Item)`
  display: flex;
  align-items: first baseline;
  white-space: break-spaces;

  &:hover {
    background: transparent;
    color: inherit;
  }

  label span {
    font-weight: 400;
  }

  label > div {
    display: flex;
    align-items: center;
  }

  && {
    /** Fix for aligning the label with the other items */
    padding-left: 6px; /* stylelint-disable-line scales/space */

    label {
      margin-top: 8px;
    }
  }
`

export const StyledPopover = styled(SelectPopover)`
  width: 265px;
`

export const StyledOption = styled(SelectOption)`
  display: flex;
  border-radius: 0;

  ${Checkmark} {
    flex: 0 0 auto;
  }

  &:hover,
  &[aria-selected='true']:not([aria-disabled='true']) {
    && {
      color: ${({ theme }) => theme.colors.foreground.primary[1]};
      background-color: ${({ theme }) => theme.colors.primaryTint[100]};
    }

    ${SecondaryText} {
      color: ${({ theme }) => theme.colors.text.light.secondary};
    }

    ${Checkmark} {
      color: ${({ theme }) => theme.colors.white};
    }
  }

  /* Selected items */
  &[data-reach-listbox-option][data-current]:not(:hover) {
    color: inherit;
    background: inherit;
  }
`

export const SelectOptionWrapper = styled.div`
  padding-left: 24px;

  [data-current] & {
    padding-left: 0;
  }
`

export const StyledOptionDelete = styled(StyledOption)`
  &:hover,
  &[data-reach-listbox-option][aria-selected='true'] {
    & div {
      color: inherit;
    }
  }
  && {
    padding-left: 20px; /** For fixing the alignment with the rest of the items */
  }
`

export const SelectOptionDeleteWrapper = styled.div`
  color: ${({ theme }) => theme.colors.red};
  padding: 8px 20px;
`
